import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  ListItemText,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import httpclient from "../../../Utils";
import { useCustomNotification } from "./useCustomNotification";

export default function AddCustomNotification() {
  const { id } = useParams(); // For edit mode
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const isEditMode = !!id;
  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { refresh } = useTokenRefresh();
  const { fetchNotifications, userList } = useCustomNotification();

  const enhancedUserList = [
    { user_id: "all", user_name: "Everyone" },
    ...userList,
  ];

  // Message validation function (mandatory)
  const validateMessage = (value) => {
    if (value.length === 0) {
      return "Message is required";
    }
    if (value.length > 200) {
      return "Message must not exceed 200 characters";
    }
    return "";
  };

  // Handle message change with validation
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    setMessageError(validateMessage(value));
  };

  // Handle user selection change with validation
  const handleUserChange = (event, newValue) => {
    // Check if "Everyone" is being selected
    const isEveryoneSelected = newValue.some((user) => user.user_id === "all");
    const wasEveryoneSelected = selectedUsers.some(
      (user) => user.user_id === "all"
    );

    // If "Everyone" is being selected, only keep "Everyone"
    if (isEveryoneSelected && !wasEveryoneSelected) {
      setSelectedUsers([{ user_id: "all", user_name: "Everyone" }]);
    }
    // If "Everyone" was already selected and trying to add other users, prevent it
    else if (wasEveryoneSelected && isEveryoneSelected) {
      // Keep only "Everyone" - prevent adding other users
      setSelectedUsers([{ user_id: "all", user_name: "Everyone" }]);
    }
    // Normal case - just update the selection
    else {
      setSelectedUsers(newValue);
    }
  };

  // Fetch templates for autocomplete in add mode
  const fetchTemplates = async () => {
    try {
      const res = await httpclient.get("/custom-notifications");
      if (res.data.status === 200) {
        setNotificationTemplates(res.data.custom_template_list || []);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const getNotificationData = async (notificationId) => {
    const res = await httpclient.get(`/custom-notifications/${notificationId}`);
    if (res.data.status === 200) {
      return res.data.data;
    }
    const appError = new Error(
      res.data.message || "Failed to fetch notification details"
    );
    appError.isAppError = true;
    throw appError;
  };

  // Handle template selection and populate fields
  const handleTemplateChange = async (event, newValue) => {
    setSelectedTemplate(newValue);
    if (newValue) {
      setTemplateLoading(true);
      try {
        const data = await getNotificationData(newValue.custom_template_id);
        setMessage(data.message || "");
        setMessageError("");
      } catch (error) {
        if (error.isAppError) {
          showNotification("error", error.message);
        } else {
          handleApiError(error);
        }
      } finally {
        setTemplateLoading(false);
      }
    } else {
      // Reset fields if cleared
      setMessage("");
      setMessageError("");
    }
  };

  useEffect(() => {
    // Populate form with fetched data in edit mode
    if (isEditMode && notificationData && userList.length > 0) {
      const data = notificationData;
      setMessage(data.message || "");
      setMessageError(validateMessage(data.message || ""));
      setSaveAsTemplate(data.is_allow_custom_template === 1);

      let selectedUserData;
      if (data.send_user_list === "all") {
        selectedUserData = [{ user_id: "all", user_name: "Everyone" }];
      } else {
        // Use send_user_list array to map selected users
        selectedUserData = userList.filter((user) =>
          data.send_user_list?.includes(user.user_id)
        );
      }
      setSelectedUsers(selectedUserData);
    } else if (!isEditMode) {
      // Reset form fields in add mode
      setMessage("");
      setMessageError("");
      setSelectedUsers([]);
      setSaveAsTemplate(false);
      setSelectedTemplate(null);
    }
  }, [id, notificationData, userList]);

  // Fetch templates in add mode
  useEffect(() => {
    if (!isEditMode) {
      fetchTemplates();
    }
  }, [id]);

  // Fetch notification details in edit mode
  useEffect(() => {
    if (isEditMode && id && !fetched) {
      const fetchNotificationDetails = async () => {
        try {
          const data = await getNotificationData(id);
          setNotificationData(data);
          setFetched(true);
        } catch (error) {
          if (error.isAppError) {
            showNotification("error", error.message);
          } else {
            if (error.response?.status === 401) {
              refresh();
            }
            handleApiError(error);
          }
          navigate("/custom-notifications");
        }
      };
      fetchNotificationDetails();
    } else if (!isEditMode) {
      setNotificationData(null);
      setFetched(false);
    }
  }, [
    isEditMode,
    id,
    fetched,
    navigate,
    showNotification,
    handleApiError,
    refresh,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const currentMessageError = validateMessage(message);

    setMessageError(currentMessageError);

    // Don't submit if there are validation errors
    if (currentMessageError) {
      return;
    }

    setLoading(true);

    let sendUserList;
    if (selectedUsers.some((user) => user.user_id === "all")) {
      sendUserList = "all";
    } else {
      sendUserList = selectedUsers.map((user) => user.user_id);
    }

    const payload = {
      message,
      send_user_list: sendUserList,
      is_allow_custom_template: saveAsTemplate ? 1 : 0,
    };

    try {
      let res;
      if (isEditMode) {
        // Edit mode - PUT request
        res = await httpclient.put(`/custom-notifications/${id}`, payload);
      } else {
        // Add mode - POST request
        res = await httpclient.post("/custom-notifications", payload);
      }

      if (res.data.status === 200) {
        showNotification("success", res.data.message);
        fetchNotifications();
        navigate("/custom-notifications");
      } else {
        showNotification("error", res.data.message);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      }
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // Check if form is valid for submit button
  const isFormValid = !messageError && selectedUsers.length > 0;

  return (
    <Paper elevation={3} sx={{ overflow: "hidden" }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? "Edit Custom Notification" : "Add Custom Notification"}
        </Typography>

        {isEditMode && (userList.length === 0 || !notificationData) ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography
              color="secondary"
              variant="caption"
              sx={{ my: 2, fontStyle: "italic" }}
            >
              Fields marked * are required
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              {!isEditMode && (
                <Autocomplete
                  options={notificationTemplates}
                  getOptionLabel={(option) => option.title}
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select from Template"
                      placeholder={
                        templateLoading
                          ? "Loading template details..."
                          : "Choose a template to populate fields"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {templateLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 3 }}
                />
              )}

              <TextField
                fullWidth
                label="Message"
                multiline
                rows={3}
                value={message}
                onChange={handleMessageChange}
                error={!!messageError}
                helperText={messageError || `${message.length}/200 characters`}
                required
                inputProps={{
                  maxLength: 200, // Prevent typing beyond 200 characters
                }}
                sx={{ mb: 3 }}
              />

              <Autocomplete
                multiple
                options={enhancedUserList}
                getOptionLabel={(option) => option.user_name}
                value={selectedUsers}
                onChange={handleUserChange}
                filterSelectedOptions
                getOptionDisabled={(option) => {
                  // Disable all options except "Everyone" when "Everyone" is selected
                  const isEveryoneSelected = selectedUsers.some(
                    (user) => user.user_id === "all"
                  );
                  return isEveryoneSelected && option.user_id !== "all";
                }}
                renderInput={(params) => (
                  <>
                    <TextField
                      label="Select Users"
                      placeholder="Users"
                      {...params}
                    />
                    <input
                      type="text"
                      required={false}
                      value={selectedUsers
                        .map((user) => user.user_id)
                        .join(",")}
                      style={{ display: "none" }}
                      onChange={() => {}} // No-op since this is controlled by Autocomplete
                    />
                  </>
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    <ListItemText primary={option.user_name} />
                  </li>
                )}
                sx={{ mb: 3 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveAsTemplate}
                    onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  />
                }
                label="Save as Custom Template"
                sx={{ mb: 3, ml: 0 }}
              />

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/custom-notifications")}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading || !isFormValid}
                >
                  {loading
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update"
                    : "Save"}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}
